// ============================================================
// SETTLING ON THE MAP WITH ITS OBJECTS (2026-10-26).
//
// A terrain Object's box is a solid every settle must meet - a marine on a generator keeps his place through a map
// edit, and comes down when it is destroyed - but three things make the plain "terrain + boxes" wrong (the review):
//
//   - A box stands where its OBJECT will stand. A settle moves the objects too (the slab under a generator deleted),
//     so the boxes are built from the objects settled on the terrain first, not from where their tokens still are.
//   - A GM-hidden object's box carries no one. Players have no box for it, so a shared figure stood on it would float
//     above bare ground on their screens - giving the object away.
//   - A change of boxes moves only its RIDERS (`ridersOf`): a wound to a generator must not drop a figure hovering on
//     the far side of the map.
//
// Each token skips its own box (`reconcileTokens`). Pure. `groundAt` is injected, as in reconcile.
// ============================================================
import { reconcileTokens, type Reconciliation } from '../battlefield/reconcile';
import { terrainSolids, type Terrain } from '../battlefield/presets';
import { solidBounds } from '../battlefield/solids';
import type { Token } from '../battlefield/tokens';
import type { Solid } from '../battlefield/types';
import { objectSolids, type ObjectSolidsInput } from './objectSolids';
import { terrainBoxOf } from './objectSpec';

/** Who the boxes belong to and where they stand: the roster, every token (the hidden ones too), the hidden ids. */
export type MapObjects = Omit<ObjectSolidsInput, 'boundary'>;

type GroundAt = (x: number, z: number) => number;

/** The visible terrain Objects' tokens, settled on the terrain's solids alone: where their boxes will stand. */
function settledObjectTokens(base: Solid[], terrain: Terrain, objects: MapObjects, groundAt: GroundAt) {
  const visible = objects.roster.filter((c) => !objects.hiddenIds?.has(c.id) && terrainBoxOf(c.sheet));
  const own = Object.fromEntries(
    visible.filter((c) => objects.tokens[c.id]).map((c) => [c.id, objects.tokens[c.id] as Token]),
  );
  return { visible, placed: reconcileTokens(own, terrain.boundary, base, groundAt).tokens };
}

/**
 * Every visible terrain Object's box, where its object will stand on this terrain. Stacked objects are not supported:
 * each object is seated on the terrain alone, so a crate on a crate is boxed at the ground.
 */
export function settledBoxes(
  terrain: Terrain,
  objects: MapObjects,
  groundAt: GroundAt,
  base: Solid[] = terrainSolids(terrain),
): Solid[] {
  const { visible, placed } = settledObjectTokens(base, terrain, objects, groundAt);
  return objectSolids({ roster: visible, tokens: placed, boundary: terrain.boundary }).solids;
}

/**
 * The tokens settled on the terrain and the visible boxes - `boxes` when the caller already holds them, else built
 * from `objects`; with neither, the terrain alone. The solids come back too, so a caller can ask who is left inside
 * one (the scene editor's swap).
 */
export function settleOnMap(
  tokens: Record<string, Token>,
  terrain: Terrain,
  objects: MapObjects | null,
  groundAt: GroundAt,
  boxes?: Solid[],
): Reconciliation & { solids: Solid[] } {
  const base = terrainSolids(terrain);
  const own = boxes ?? (objects ? settledBoxes(terrain, objects, groundAt, base) : []);
  // The terrain's own array when there are no boxes, as `withObjectSolids` keeps it.
  const solids = own.length ? [...base, ...own] : base;
  return { ...reconcileTokens(tokens, terrain.boundary, solids, groundAt), solids };
}

/**
 * What a settle builds its boxes from: the objects last seen, with the tokens being settled laid over theirs (the
 * freshest places) and, when given, this settle's own roster. A scene cut writes its marks and loads its map in one
 * batch, before the shell has seen the new cast - without the plan's roster its objects would have no boxes, and
 * whoever stood on them would drop to the ground. Nothing known and no roster given: no objects at all.
 */
export function objectsForSettle(
  known: MapObjects | null,
  tokens: Record<string, Token>,
  roster: MapObjects['roster'] | undefined,
): MapObjects | null {
  if (!known && !roster) return null;
  return {
    roster: roster ?? known?.roster ?? [],
    tokens: { ...known?.tokens, ...tokens },
    hiddenIds: known?.hiddenIds,
  };
}

/** A box's place and size, to tell a changed box from the same one rebuilt. */
const shapeOf = (s: Solid) => {
  const { min, max } = solidBounds(s);
  return [min.x, min.y, min.z, max.x, max.y, max.z].map((n) => n.toFixed(3)).join(',');
};

/** The boxes that appeared, went or changed between the two sets - both versions of a changed one. */
function changedBoxes(before: readonly Solid[], after: readonly Solid[]): Solid[] {
  const was = new Map(before.map((s) => [s.id, shapeOf(s)]));
  const now = new Map(after.map((s) => [s.id, shapeOf(s)]));
  const changed = new Set([...was.keys(), ...now.keys()].filter((id) => was.get(id) !== now.get(id)));
  return [...before, ...after].filter((s) => changed.has(s.id));
}

/** Does the token stand within the box's footprint (its own footprint counted)? */
function standsWithin(t: Token, box: Solid): boolean {
  const { min, max } = solidBounds(box);
  const r = t.footprint;
  return t.x >= min.x - r && t.x <= max.x + r && t.z >= min.z - r && t.z <= max.z + r;
}

/** The ids of the tokens a change of boxes may move: whoever stands where a box was, or where one now is. */
export function ridersOf(before: readonly Solid[], after: readonly Solid[], tokens: Record<string, Token>): string[] {
  const changed = changedBoxes(before, after);
  if (!changed.length) return [];
  return Object.values(tokens)
    .filter((t) => changed.some((box) => standsWithin(t, box)))
    .map((t) => t.id);
}
